import { IconPaperclip } from "./icons.js";
import type { ReactNode } from "react";

import type { LongPress } from "../lib/useLongPress.js";
import type { SelectIntent } from "../lib/useMessageSelection.js";
import type { MailAddress, MessageListPage, MessageSummary } from "../lib/types.js";

interface MessageListProps {
  page: MessageListPage | null;
  loading: boolean;
  error: string | null;
  selectedUid: number | null;
  /** true in Verzonden/Concepten: daar is de ontvanger de interessante partij. */
  showRecipients: boolean;
  onOpen: (message: MessageSummary) => void;
  onPrevious: () => void;
  onNext: () => void;

  // ── Selectie ──────────────────────────────────────────────────────────────
  selection: ReadonlySet<number>;
  /** Keuzemodus: op mobiel staan de vinkjes dan altijd zichtbaar. */
  selectionMode: boolean;
  onSelect: (uid: number, intent: SelectIntent) => void;
  longPress: LongPress;

  // ── Slepen ────────────────────────────────────────────────────────────────
  /** false op mobiel; daar zou een sleep met het keuzegebaar vechten. */
  dragEnabled: boolean;
  onDragStart: (message: MessageSummary, event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

const MONTHS = [
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

const DAY_MS = 86_400_000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Datums in de lijst zijn er om berichten uit elkaar te houden, niet om
 * precies te zijn: vandaag toont een tijd, gisteren een woord, ouder een datum.
 */
function formatListDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);

  if (days === 0) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  }
  if (days === 1) return "gisteren";

  const month = MONTHS[date.getMonth()] ?? "";
  if (date.getFullYear() === now.getFullYear()) return `${date.getDate()} ${month}`;
  return `${date.getDate()} ${month} ${date.getFullYear()}`;
}

function fullDate(iso: string): string | undefined {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString("nl-NL");
}

function displayName(list: MailAddress[]): string {
  const first = list[0];
  if (first === undefined) return "(onbekend)";
  const label = first.name.trim() !== "" ? first.name : first.address;
  return list.length > 1 ? `${label} +${list.length - 1}` : label;
}

function subjectOf(message: MessageSummary): string {
  return message.subject.trim() === "" ? "(geen onderwerp)" : message.subject;
}

function Skeletons(): ReactNode {
  return (
    <ul className="message-list" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
        <li key={index} className="message-row message-row--skeleton">
          <span className="skeleton skeleton--sender" />
          <span className="skeleton skeleton--subject" />
        </li>
      ))}
    </ul>
  );
}

export function MessageList({
  page,
  loading,
  error,
  selectedUid,
  showRecipients,
  onOpen,
  onPrevious,
  onNext,
  selection,
  selectionMode,
  onSelect,
  longPress,
  dragEnabled,
  onDragStart,
  onDragEnd,
}: MessageListProps): ReactNode {
  if (error !== null) {
    return <p className="pane-message pane-message--error">{error}</p>;
  }

  // Alleen skeletons als er nog niets staat; bij bladeren of pollen laten we de
  // oude lijst staan, dat scheelt een flits.
  if (page === null) return loading ? <Skeletons /> : null;

  if (page.messages.length === 0) {
    return <p className="pane-message">Deze map is leeg.</p>;
  }

  const first = page.total - page.offset;
  const last = Math.max(1, first - page.messages.length + 1);
  const hasPrevious = page.offset > 0;
  const hasNext = page.offset + page.messages.length < page.total;

  function handleClick(message: MessageSummary, e: React.MouseEvent): void {
    // De klik die op een geslaagde long-press volgt mag het bericht niet ook
    // nog openen.
    if (longPress.consumeClick()) return;

    if (e.shiftKey) {
      onSelect(message.uid, "range");
      return;
    }
    // metaKey dekt cmd-klik op de Mac; daar geeft ctrl-klik een contextmenu.
    if (e.ctrlKey || e.metaKey || selectionMode) {
      onSelect(message.uid, "toggle");
      return;
    }
    onOpen(message);
  }

  return (
    <>
      {/*
        Bewust géén role="listbox" met role="option": een option hoort een blad
        te zijn, en deze rijen bevatten een checkbox én een knop. Met gewone
        lijstsemantiek vertelt de checkbox zelf of het bericht geselecteerd is,
        en dat is precies wat een screenreader nodig heeft.
      */}
      <ul
        className={[
          "message-list",
          loading ? "is-refreshing" : "",
          selectionMode ? "is-selecting" : "",
        ]
          .filter((part) => part !== "")
          .join(" ")}
      >
        {page.messages.map((message) => {
          const people = showRecipients ? message.to : message.from;
          const isSelected = selection.has(message.uid);
          return (
            <li key={message.uid}>
              <div
                className={[
                  "message-row",
                  message.seen ? "" : "is-unread",
                  message.uid === selectedUid ? "is-active" : "",
                  isSelected ? "is-selected" : "",
                ]
                  .filter((part) => part !== "")
                  .join(" ")}
                // Een niet-geselecteerd bericht slepen pakt alleen dat bericht;
                // een geselecteerd bericht neemt de hele selectie mee. Zo doen
                // Verkenner en Outlook het ook.
                draggable={dragEnabled && (selection.size === 0 || isSelected)}
                onDragStart={(e) => onDragStart(message, e)}
                onDragEnd={onDragEnd}
              >
                <label className="message-check">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelect(message.uid, "toggle")}
                    aria-label={`${subjectOf(message)} selecteren`}
                  />
                </label>

                <button
                  type="button"
                  className="message-open"
                  aria-current={message.uid === selectedUid ? "true" : undefined}
                  onClick={(e) => handleClick(message, e)}
                  {...longPress.handlers(message.uid)}
                >
                  <span className="message-top">
                    <span className="message-sender">{displayName(people)}</span>
                    <time className="message-date" dateTime={message.date} title={fullDate(message.date)}>
                      {formatListDate(message.date)}
                    </time>
                  </span>
                  <span className="message-bottom">
                    <span className="message-subject">{subjectOf(message)}</span>
                    {message.hasAttachments && (
                      <span className="message-clip" title="Bevat bijlagen">
                        <IconPaperclip className="icon-clip" />
                        <span className="visually-hidden">Bevat bijlagen</span>
                      </span>
                    )}
                  </span>
                  {message.preview !== undefined && message.preview.trim() !== "" && (
                    <span className="message-preview">{message.preview}</span>
                  )}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="pager">
        <button type="button" className="button button--ghost" onClick={onPrevious} disabled={!hasPrevious}>
          Vorige
        </button>
        <span className="pager-status">
          {last}–{first} van {page.total}
        </span>
        <button type="button" className="button button--ghost" onClick={onNext} disabled={!hasNext}>
          Volgende
        </button>
      </div>
    </>
  );
}
